import { createSelector } from '@ngrx/store';
import type { Selector } from '@ngrx/store';

import { namedProjector } from './selector-utils';
import type { BaseMetaState, MetaState, PaginationState } from './types';

export interface MetaAdapter<S extends MetaState> {
  markAsPending: (state: S) => S;
  markAsLoaded: (state: S) => S;
  setError: (state: S, error: any) => S;
}

export const getMetaAdapter = <T extends MetaState>() =>
  <MetaAdapter<T>>{
    markAsPending: state => ({
      ...state,
      pending: true,
      failed: false,
      error: null,
    }),
    markAsLoaded: state => ({
      ...state,
      pending: false,
      loaded: true,
      failed: false,
      error: null,
    }),
    setError: (state, error) => ({
      ...state,
      pending: false,
      loaded: true,
      failed: true,
      error,
    }),
  };

export const metaAdapter = getMetaAdapter();

export const metaInitState: BaseMetaState = {
  pending: false,
  loaded: false,
  failed: false,
  error: null,
};

export const paginationInitState: PaginationState = {
  page: null,
  limit: null,
  totalCount: null,
};

export const getMetaSelectors = (baseSelector: Selector<any, MetaState>, namespace: string) => {
  const isPending = createSelector(
    baseSelector,
    namedProjector(`${namespace}IsPending`, ({ pending }) => pending),
  );
  const isLoaded = createSelector(
    baseSelector,
    namedProjector(`${namespace}IsLoaded`, ({ loaded }) => loaded),
  );
  const error = createSelector(
    baseSelector,
    namedProjector(`${namespace}Error`, ({ error }) => error),
  );
  const isFailed = createSelector(
    baseSelector,
    namedProjector(`${namespace}IsFailed`, ({ failed }) => failed),
  );
  const meta = createSelector(
    isPending,
    isLoaded,
    error,
    isFailed,
    (isPending, isLoaded, error, isFailed) => ({ isPending, isLoaded, error, isFailed }),
  );
  return { isPending, isLoaded, error, isFailed, meta };
};
